// TrainingDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import SingleLossChart from '../components/SingleLossChart';
import CloudWatchChart from '../components/CloudWatchChart';
import html2canvas from 'html2canvas';
import '../dashboard.css';

// === 新增：与 SimulationDashboard 一致的提示组件 ===
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';

// 日志与 GPU 状态轮询间隔（毫秒）→ 30 秒
const POLL_MS = 30000;

const TrainingDashboard = () => {
  const [formValues, setFormValues] = useState({
    training_data_path: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/trainingdata/',
    optimizer: 'AdamW',
    save_model_name: 'SPTnet_v1',
    path_saved_model: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/train/',
    momentum: '0.9',
    learning_rate: '0.0001',
    batch_size: '24',
    use_gpu: true,
    image_size: '64',
    number_of_frame: '30',
    num_queries: '30',
    trained_model_path: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/train/SPTnet_v1_0627',
    test_data_path: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/testingdata/training_perlin_30frame_5queries_10.mat',
    test_data_file: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/testingdata/training_perlin_30frame_5queries_10.mat',
    inference_output_file: '/home/ubuntu/deployment_v1/SPTnet_AWS_deployment_v1/train/inference_results/result_training_perlin_30frame_5queries_10.mat',
    number_of_test_videos: '6'
  });

  const [gpuStatus, setGpuStatus] = useState('Loading GPU info...');
  const [trainingLogs, setTrainingLogs] = useState('Waiting for logs...');

  // 记录后端返回的 Last-Modified，用于 If-Modified-Since
  const lastModifiedRef = useRef(null);
  const gridRef = useRef(null);

  // === 用于四个下拉的数据源 ===
  const [trainedModelFiles, setTrainedModelFiles] = useState([]);       // /train/trainedmodel/*
  const [testingDataFiles, setTestingDataFiles] = useState([]);         // /testingdata/*
  const [inferenceResultFiles, setInferenceResultFiles] = useState([]); // /train/inference_results/*

  // ✅ 定义 token，避免 ESLint “token is not defined”
  const token =
    (typeof window !== 'undefined' && (localStorage.getItem('accessToken') || localStorage.getItem('idToken'))) ||
    '';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ✅ 只改这个函数：把 formValues 存成 training_params.json，algorithm 用 SPTComparison 选的
  const handleSubmitParameters = async () => {
    try {
      // 先从 SPTComparison 存的算法名里拿（你那边 localStorage 设置的 key 如果不是这个，就把 'selectedAlgorithm' 改成你自己的）
      const selectedAlgorithm =
        (typeof window !== 'undefined' &&
          (localStorage.getItem('selectedAlgorithm') ||
            localStorage.getItem('comparisonAlgorithm'))) ||
        '';

      // 优先用 SPTComparison 里选的算法名，其次才是当前界面 save_model_name，最后兜底 SPTNet_v1
      const algorithm =
        (selectedAlgorithm && selectedAlgorithm.trim()) ||
        (formValues.save_model_name && formValues.save_model_name.trim()) ||
        'SPTNet_v1';

      // 按 SaveParamsView 的“形式 2”来传：
      // { algorithm, file_name, params }
      const payload = {
        algorithm,
        file_name: 'training_params.json',
        params: formValues,
        // username 不传也可以，后端会从 request.user 里拿
      };

      const res = await fetch('/api/params/save/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      // 先拿文本，看是不是 JSON
      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('❌ 后端返回的不是 JSON，原始内容为：', text);
        toast.error('❌ 保存失败：后端返回的不是 JSON（请检查 /api/params/save/）');
        return;
      }

      console.log('✅ save training_params.json response:', data);

      if (res.ok) {
        toast.success(
          `✅ training_params.json 已保存 (job_id = ${data.job_id ?? 'N/A'})`
        );
      } else {
        toast.error(
          `⚠️ 保存失败：${data.detail || data.message || 'Unknown error'}`
        );
      }
    } catch (err) {
      console.error('❌ Failed to save training params:', err);
      toast.error('❌ Failed to save training params!');
    }
  };

  const handleStartTraining = async () => {
    try {
      const res = await fetch('/api/start-training/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });
      const data = await res.json();
      console.log('Training started:', data);
      if (res.ok) toast.success('🚀 Training started');
      else toast.error(`⚠️ Failed to start training: ${data?.message || 'Unknown error'}`);
    } catch (err) {
      console.error('❌ Failed to start training:', err);
      toast.error('❌ Failed to start training');
    }
  };

  const handleEarlyStop = async () => {
    try {
      const res = await fetch('/api/stop-training/', { method: 'POST' });
      const data = await res.json();
      if (data.status === 'stopped') {
        console.log(`🛑 Stopped training process PID: ${data.pid}`);
        toast.success(`✋ Training process PID ${data.pid} has been stopped`);
      } else {
        console.warn('⚠️ Could not stop training:', data.message);
        toast.warn(`⚠️ Stop failed: ${data.message}`);
      }
    } catch (err) {
      console.error('❌ Failed to stop training:', err);
      toast.error('❌ Request failed while stopping training');
    }
  };

  const handleStartInference = async () => {
    try {
      const res = await fetch('/api/start-inference/', { method: 'POST' });
      const data = await res.json();
      if (data.status === 'started') {
        console.log('🧠 Inference started, PID:', data.pid);
        toast.success(`🧠 Inference started (PID ${data.pid})`);
      } else {
        toast.warn(`⚠️ Failed to start inference: ${data.message}`);
      }
    } catch (err) {
      console.error('❌ Failed to start inference:', err);
      toast.error('❌ Error starting inference');
    }
  };

  const handleStartVisualization = async () => {
    try {
      const res = await fetch('/api/start-visualization/', { method: 'POST' });
      const data = await res.json();
      if (data.status === 'started') {
        console.log('📊 Visualization started (PID):', data.pid);
        toast.success(`📊 Visualization script started (PID ${data.pid})`);
      } else {
        toast.warn(`⚠️ Failed to start visualization: ${data.message}`);
      }
    } catch (err) {
      console.error('❌ Failed to start visualization:', err);
      toast.error('❌ Error starting visualization');
    }
  };

  const handleExportDashboard = () => {
    if (gridRef.current) {
      html2canvas(gridRef.current, { scale: 2 }).then((canvas) => {
        const link = document.createElement("a");
        link.download = "Training_Logs_dashboard.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success('✅ Exported dashboard PNG');
      }).catch(() => {
        toast.error('❌ Export failed');
      });
    }
  };

  // --- 拉取 GPU 状态
  const fetchGpuStatus = () => {
    fetch('/api/gpu-status/')
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.raw === 'string') {
          setGpuStatus(prev => (prev !== data.raw ? data.raw : prev));
        }
      })
      .catch(err => {
        console.error("❌ GPU Status Error:", err);
        setGpuStatus('⚠️ Error fetching GPU status');
      });
  };

  // --- 拉取训练日志（仅在文件更新时刷新）
  const fetchTrainingLogs = () => {
    const headers = {};
    if (lastModifiedRef.current) {
      headers['If-Modified-Since'] = lastModifiedRef.current;
    }
    fetch('/api/training-logs/?last=200', { headers })
      .then((res) => {
        if (res.status === 304) {
          return null;
        }
        const lm = res.headers.get('Last-Modified');
        if (lm) lastModifiedRef.current = lm;
        return res.text();
      })
      .then((text) => {
        if (text !== null && typeof text === 'string') {
          setTrainingLogs(prev => (prev !== text ? text : prev));
        }
      })
      .catch(() => {
        setTrainingLogs('⚠️ Error fetching training logs');
      });
  };

  // === 拉取文件列表（用于四个下拉） ===
  const fetchFiles = async (type, setter, targetField) => {
    try {
      const res = await fetch(`/api/list-files/?type=${encodeURIComponent(type)}`);
      const data = await res.json();
      if (!res.ok) {
        console.error('❌ list-files error:', data?.error || res.statusText);
        setter([]);
        return;
      }
      setter(data.files || []);
      if (data.files && data.files.length > 0) {
        setFormValues(prev => {
          const current = prev[targetField] || '';
          const paths = new Set((data.files || []).map(x => x.path));
          return paths.has(current) ? prev : { ...prev, [targetField]: data.files[0].path };
        });
      }
    } catch (e) {
      console.error(`❌ fetch ${type} failed:`, e);
      setter([]);
    }
  };

  // 初次加载 + 轮询（30 秒）
  useEffect(() => {
    fetchGpuStatus();
    fetchTrainingLogs();
    const id = setInterval(() => {
      fetchGpuStatus();
      fetchTrainingLogs();
    }, POLL_MS);

    fetchFiles('trained_model', setTrainedModelFiles, 'trained_model_path');
    fetchFiles('testingdata', setTestingDataFiles, 'test_data_path');
    fetchFiles('testingdata', setTestingDataFiles, 'test_data_file');
    fetchFiles('inference_results', setInferenceResultFiles, 'inference_output_file');

    return () => clearInterval(id);
  }, []); // 只初始化一次

  // 小型通用下拉（替换四个输入框）
  const ServerFileSelect = ({ label, files, fieldName }) => (
    <div className="flex justify-between items-center col-span-2 text-sm">
      <label>{label}</label>
      <select
        name={fieldName}
        value={formValues[fieldName] || ''}
        onChange={handleChange}
        className="w-[60%] p-1 border border-slate-300 rounded bg-white text-slate-800 focus:ring focus:ring-cyan-400 font-mono"
      >
        {(!files || files.length === 0) && <option value="">(空)</option>}
        {files && files.map((f) => (
          <option key={f.path} value={f.path}>
            {fieldName === 'trained_model_path' ? f.name.replace(/\.pth$/i, '') : f.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="w-screen h-screen flex bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 text-slate-800 overflow-hidden">
      {/* 左半部分 */}
      <div className="w-1/2 h-full flex flex-col p-6 space-y-6 overflow-y-auto">
        {/* 参数输入 */}
        <div className="border border-slate-300 p-6 rounded-2xl shadow-lg bg-gradient-to-br from-white to-slate-50">
          <h2 className="text-xl font-extrabold mb-4 text-black border-b border-slate-300 pb-1">⚙️ Training Parameters</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              'training_data_path',
              'path_saved_model',
              'save_model_name',
              'momentum',
              'learning_rate',
              'batch_size',
              'image_size',
              'number_of_frame',
              'num_queries',
            ].map((field) => (
              <div key={field} className="flex justify-between items-center">
                <label>{field}</label>
                <input
                  name={field}
                  value={formValues[field]}
                  onChange={handleChange}
                  className="w-[60%] p-1 border border-slate-300 rounded bg-white text-slate-800 focus:ring focus:ring-cyan-400 font-mono"
                />
              </div>
            ))}
            <div className="flex justify-between items-center">
              <label>optimizer</label>
              <select
                name="optimizer"
                value={formValues.optimizer}
                onChange={handleChange}
                className="w-[60%] p-1 border border-slate-300 rounded bg-white text-slate-800 focus:ring focus:ring-cyan-400"
              >
                <option value="SGD">optimizer_SGD</option>
                <option value="Adam">optimizer_Adam</option>
                <option value="AdamW">optimizer_AdamW</option>
              </select>
            </div>
            <div className="flex justify-between items-center col-span-2">
              <label>use_gpu</label>
              <input
                type="checkbox"
                name="use_gpu"
                checked={formValues.use_gpu}
                onChange={handleChange}
                className="ml-2"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button className="px-4 py-2 bg-slate-700 text-white rounded-full hover:bg-slate-600 transition" onClick={handleSubmitParameters}>💾 Submit Parameters</button>
            <button className="px-4 py-2 bg-cyan-700 text-white rounded-full hover:bg-cyan-600 transition" onClick={handleStartTraining}>🚀 Start Model Training</button>
            <button className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-500 transition" onClick={handleEarlyStop}>✋ Training Early Stop</button>
          </div>

          <div className="w-full mt-4 grid grid-cols-2 gap-4">
            <ServerFileSelect
              label="trained_model_path"
              files={trainedModelFiles}
              fieldName="trained_model_path"
            />
            <ServerFileSelect
              label="test_data_path"
              files={testingDataFiles}
              fieldName="test_data_path"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button className="px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-500 transition" onClick={handleStartInference}>🧠 Start Inference</button>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-full hover:bg-yellow-500 transition" onClick={handleStartVisualization}>📊 Start Visualization</button>
          </div>

          <div className="w-full mt-4 grid grid-cols-2 gap-4 text-sm">
            <ServerFileSelect
              label="test_data_file"
              files={testingDataFiles}
              fieldName="test_data_file"
            />
            <ServerFileSelect
              label="inference_output_file"
              files={inferenceResultFiles}
              fieldName="inference_output_file"
            />
            <div className="flex justify-between items-center col-span-2">
              <label>number_of_test_videos</label>
              <input
                type="number"
                name="number_of_test_videos"
                value={formValues.number_of_test_videos || ''}
                onChange={handleChange}
                className="w-[60%] p-1 border border-slate-300 rounded bg-white text-slate-800 focus:ring focus:ring-cyan-400 font-mono"
              />
            </div>
          </div>
        </div>

        {/* GPU 状态块 - 深蓝黑背景 */}
        <div className="w-full h-[23vh] border border-slate-700 p-4 rounded-xl shadow bg-[#0f172a] flex flex-col">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100 mb-2">🖥️ <span>GPU Status</span></h2>
          <pre className="text-xs text-green-300 font-mono overflow-auto whitespace-pre leading-relaxed">{gpuStatus}</pre>
        </div>
      </div>

      {/* 右半部分 */}
      <div className="w-1/2 h-full flex flex-col p-6 space-y-6 overflow-y-auto">
        {/* Training Logs 块 - 深蓝黑背景 */}
        <div className="w-full h-[30vh] border border-slate-700 p-4 rounded-xl shadow bg-[#0f172a] flex flex-col">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100 mb-2">🗒️ <span>Training Logs</span></h2>
          {/* 关键修改：确保进度条一行完整显示，不换行 */}
          <pre className="text-xs text-green-300 font-mono overflow-auto whitespace-pre leading-relaxed">{trainingLogs}</pre>
        </div>

        <div className="w-full h-[62vh] border border-slate-300 p-6 rounded-2xl shadow-lg bg-gradient-to-br from-white to-slate-50 relative">
          <div className="flex justify-between items-start mb-2">
            <h2
              className="text-lg font-bold text-black cursor-pointer hover:underline"
              onClick={() => window.location.href = 'http://localhost:3000/dashboard'}
            >
              📊 Training Dashboard
            </h2>
            <button onClick={handleExportDashboard} className="text-sm text-slate-700 px-3 py-1 rounded-full border border-slate-400 hover:bg-slate-100">⬇️ Export</button>
          </div>

          {/* loss 图表区域 */}
          <div className="dashboard-grid" ref={gridRef}>
            <div className="chart-card text-sm" style={{ height: '240px' }}>
              <SingleLossChart title=" Total Loss" trainKey="t_loss" valKey="v_loss" />
            </div>
            <div className="chart-card text-sm" style={{ height: '240px' }}>
              <SingleLossChart title=" Classification Loss" trainKey="t_cls_loss" valKey="v_cls_loss" />
            </div>
            <div className="chart-card text-sm" style={{ height: '240px' }}>
              <SingleLossChart title=" Coordinate Loss" trainKey="t_coor_loss" valKey="v_coor_loss" />
            </div>
            <div className="chart-card text-sm" style={{ height: '240px' }}>
              <SingleLossChart title=" Hurst Loss" trainKey="t_hurst_loss" valKey="v_hurst_loss" />
            </div>
            <div className="chart-card text-sm" style={{ height: '240px' }}>
              <SingleLossChart title=" Diff Loss" trainKey="t_diff_loss" valKey="v_diff_loss" />
            </div>
            <div className="chart-card text-sm" style={{ height: '240px', justifyContent: 'center', color: '#999', textAlign: 'center' }}>
              {/* 预留位 */}
              None
            </div>
          </div>
        </div>
      </div>

      {/* 与 SimulationDashboard 一致的提示容器 */}
      <ToastContainer />
    </div>
  );
};

export default TrainingDashboard;
